import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ShieldCheck,
  Ticket,
  UserCog,
  Building2,
  MessageCircle,
  ReceiptText,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';
import logo from '../../assets/image/system/logo.png';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

type AdminNavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  testId: string;
};

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isOpen,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const logout = useAuthStore((state) => state.logout);
  const { t } = useLanguage();
  const webVersion = import.meta.env.VITE_WEB_VERSION || 'v0.0.0';

  const items: AdminNavItem[] = [
    {
      name: t('adminConsole.nav.members', undefined, 'Admin Members'),
      href: '/admin/members',
      icon: UserCog,
      testId: 'admin-sidebar-nav-members',
    },
    {
      name: t('adminConsole.nav.vouchers', undefined, 'Vouchers'),
      href: '/admin/vouchers',
      icon: Ticket,
      testId: 'admin-sidebar-nav-vouchers',
    },
    {
      name: t('adminConsole.nav.companies', undefined, 'Companies & Subscriptions'),
      href: '/admin/companies-subscriptions',
      icon: Building2,
      testId: 'admin-sidebar-nav-companies',
    },
    {
      name: t('adminConsole.nav.lynkPayments', undefined, 'Lynk Payment History'),
      href: '/admin/lynk-payment-events',
      icon: ReceiptText,
      testId: 'admin-sidebar-nav-lynk-payments',
    },
    {
      name: t('adminConsole.nav.chatSupport', undefined, 'Chat Support'),
      href: '/admin/chat-support',
      icon: MessageCircle,
      testId: 'admin-sidebar-nav-chat-support',
    },
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity md:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      <div
        data-test-id="admin-sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-[#1a1d21] text-white transition-all duration-300 transform md:translate-x-0 md:fixed md:flex md:flex-col h-full border-r border-gray-800',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'w-20' : 'w-64',
        )}
      >
        <div
          className={cn(
            'relative flex items-center h-16 border-b border-gray-800 justify-center',
            isCollapsed ? 'px-0' : 'px-4',
          )}
        >
          {!isCollapsed ? (
            <img src={logo} alt={t('sidebar.logoAlt')} className="h-12 w-auto px-1" />
          ) : (
            <span className="text-xl font-bold">MK</span>
          )}
          <button
            onClick={onClose}
            data-test-id="admin-sidebar-close-mobile"
            className="absolute right-4 md:hidden text-gray-400 hover:text-white focus:outline-none"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Console badge */}
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-3 border-b border-gray-800/80 bg-gradient-to-r from-amber-500/10 via-transparent to-transparent',
            isCollapsed && 'justify-center px-2',
          )}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500/20 ring-1 ring-amber-500/40">
            <ShieldCheck className="h-4 w-4 text-amber-300" aria-hidden="true" />
          </span>
          {!isCollapsed && (
            <div className="leading-tight">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-300/90">
                {t('adminConsole.badge.title', undefined, 'Admin Console')}
              </div>
              <div className="text-[10px] text-gray-400">
                {t('adminConsole.badge.subtitle', undefined, 'Platform-level controls')}
              </div>
            </div>
          )}
        </div>

        <nav
          className={cn(
            'flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden',
            'scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent',
            'scrollbar-gutter-stable',
            '[&::-webkit-scrollbar]:w-2',
            '[&::-webkit-scrollbar-track]:bg-transparent',
            '[&::-webkit-scrollbar-thumb]:bg-gray-500',
            '[&::-webkit-scrollbar-thumb]:rounded-full',
            'hover:[&::-webkit-scrollbar-thumb]:bg-gray-600',
          )}
        >
          {items.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => onClose()}
              data-test-id={item.testId}
              title={isCollapsed ? item.name : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center py-2 text-sm font-medium rounded-md transition-colors',
                  isCollapsed ? 'justify-center px-2' : 'px-4',
                  isActive
                    ? 'bg-amber-500/15 text-amber-100 ring-1 ring-amber-500/30'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                )
              }
            >
              <item.icon className={cn('h-5 w-5', !isCollapsed && 'mr-3')} aria-hidden="true" />
              {!isCollapsed && item.name}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-800 space-y-2">
          <NavLink
            to="/"
            data-test-id="admin-sidebar-back-to-app"
            title={isCollapsed ? t('adminConsole.backToApp', undefined, 'Back to app') : undefined}
            className={cn(
              'flex items-center w-full py-2 text-sm font-medium rounded-md transition-colors',
              'text-gray-200 bg-gray-800/60 hover:bg-gray-800 hover:text-white',
              isCollapsed ? 'justify-center px-2' : 'px-4',
            )}
          >
            <ArrowLeft className={cn('h-4 w-4', !isCollapsed && 'mr-3')} aria-hidden="true" />
            {!isCollapsed && t('adminConsole.backToApp', undefined, 'Back to app')}
          </NavLink>

          <button
            onClick={onToggleCollapse}
            data-test-id="admin-sidebar-collapse-toggle"
            className="hidden md:flex items-center w-full px-4 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors justify-center"
            title={
              isCollapsed
                ? t('common.actions.expandSidebar')
                : t('common.actions.collapseSidebar')
            }
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <div className="flex items-center w-full">
                <ChevronLeft className="mr-3 h-5 w-5" />
                <span>{t('common.actions.collapse')}</span>
              </div>
            )}
          </button>

          <button
            onClick={() => logout()}
            data-test-id="admin-sidebar-logout-button"
            className={cn(
              'flex items-center w-full py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors',
              isCollapsed ? 'justify-center px-2' : 'px-4',
            )}
            title={isCollapsed ? t('common.actions.logout') : undefined}
          >
            <LogOut className={cn('h-5 w-5', !isCollapsed && 'mr-3')} aria-hidden="true" />
            {!isCollapsed && t('common.actions.logout')}
          </button>
          <div
            className={cn(
              'text-[11px] text-gray-500',
              isCollapsed ? 'text-center px-1' : 'px-4 text-left',
            )}
            title={webVersion}
          >
            {webVersion}
          </div>
        </div>
      </div>
    </>
  );
};
